import { t } from '../i18n';
import type { ApiErrorBody } from './types';

/**
 * Тонкая обёртка над fetch. Всё, что должен знать остальной код: путь,
 * тело и тип ответа. Токен, заголовки и разбор ошибок — здесь.
 */

/** База без хвоста /api: префикс уже есть в путях запросов. */
const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

/**
 * Токен живёт только в памяти вкладки — см. комментарий в session.ts.
 * Модуль хранит его сам, чтобы заголовок ставился в одном месте.
 */
let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

/** Ошибка запроса в виде, пригодном для показа человеку. */
export class ApiError extends Error {
  /** 0 = до сервера не дошли: нет сети, отвалился туннель, CORS. */
  readonly status: number;
  /** Подробности от ValidationPipe: по строке на непрошедшее поле. */
  readonly details: string[];
  /** Машинное имя ошибки с сервера; null у пятисоток и у обрыва сети. */
  readonly code: string | null;
  readonly body: ApiErrorBody | null;

  constructor(
    status: number,
    message: string,
    details: string[] = [],
    body: ApiErrorBody | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.code = body?.error_code ?? null;
    this.body = body;
  }

  /** Заявку кто-то изменил, пока модератор смотрел на неё. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** Сессия протухла: помогает только перезапуск приложения. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Нет прав: гость без вайтлиста или не та роль. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  params?: QueryParams;
  signal?: AbortSignal;
}

function buildUrl(path: string, params?: QueryParams): string {
  const url = `${BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!params) return url;

  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    query.set(key, String(value));
  }
  const qs = query.toString();
  return qs ? `${url}?${qs}` : url;
}

/** Nest кладёт человеческий текст в message; ValidationPipe — список строк. */
function readError(status: number, body: ApiErrorBody | null): ApiError {
  const raw = body?.message;
  const details = Array.isArray(raw) ? raw : [];
  const message = (typeof raw === 'string' && raw) || details[0] || defaultMessage(status);
  return new ApiError(status, message, details, body);
}

function defaultMessage(status: number): string {
  if (status === 401) return t('api.error.unauthorized');
  if (status === 403) return t('api.error.forbidden');
  if (status === 404) return t('api.error.notFound');
  if (status === 409) return t('api.error.conflict');
  if (status === 413) return t('api.error.tooLarge');
  if (status === 429) return t('api.error.tooManyRequests');
  if (status >= 500) return t('api.error.server');
  return t('api.error.request');
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  /*
   * В разработке фронт открывают через туннель, и ngrok на первый запрос с
   * каждого нового устройства отдаёт свою страницу-предупреждение. Для
   * запроса к API это означает HTML вместо JSON: safeParse вернёт null, и
   * игрок увидит невнятную ошибку вместо ответа сервера. Заголовок её снимает.
   *
   * Заглушку на ЗАГРУЗКЕ приложения он НЕ убирает: тот запрос делает сам
   * вебвью, а не этот код, и заголовки к нему приписать неоткуда — там
   * помогает только кнопка «Visit Site» или туннель без заглушки.
   *
   */

  let response: Response;
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      headers,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    // Отмену запроса пробрасываем как есть: это не ошибка сети.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, t('api.error.network'));
  }

  // 204 и пустое тело — законный ответ на удаление.
  const text = await response.text();
  const parsed: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    throw readError(response.status, isErrorBody(parsed) ? parsed : null);
  }

  return parsed as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return typeof value === 'object' && value !== null;
}

export const api = {
  get: <T>(path: string, params?: QueryParams, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', params, signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body: body ?? {}, signal }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PATCH', body: body ?? {}, signal }),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'PUT', body: body ?? {}, signal }),
  /** Тело у DELETE необязательное: им уходит причина отправки в архив. */
  delete: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'DELETE', body, signal }),
};

/** Текст ошибки для показа: сюда можно отдать что угодно из catch. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return t('api.error.unknown');
}
